import React, { useEffect, useMemo, useState } from 'react'
import classes from './classDetails.module.css'
import { Button, ButtonIcon } from '../../../components/Button/Button'
import { ReactComponent as HeartIcon } from '../../../assets/icons/heart.svg'
import { ClassCard } from '../../../components/Cards/GeneralCard/Card'
import Footer from '../../../components/Footer/Footer'
import Modal from '../../../components/Modal/Modal'
import { ReactComponent as CancelIcon } from '../../../assets/icons/close.svg'
import { ValidatedFormInput } from '../../../components/Form/Inputs/Inputs'
import { Dropdown } from '../../../components/Form/Dropdown/Dropdown'
import {
    convertToFormatted,
    getFreeClassesLeft,
    getImageURL,
} from '../../../util/functions'
import { AuthenticatedHeader } from '../../../components/Header/AuthenticatedHeader'
import { useDispatch, useSelector } from 'react-redux'
import { classDetailsActions, classDetailsSelectors } from './classDetailsSlice'
import { getClass } from './classDetailsApi'
import moment from 'moment'
import placeholder from '../../../assets/images/class-placeholder.svg'
import { browseClassesSelectors } from '../BrowseClasses/browseClassesSlice'
import { userActions, userSelectors } from '../../../redux/userSlice'
import { addClassToCart } from '../../Cart/cartApi'
import { Formik } from 'formik'
import { InviteUserSchema } from '../../../util/formSchemas'
import Loading from '../../../components/loaders/Loading'
import Image from '../../../components/Image/Image'
import notification from '../../../components/Notification/notification'
import CardLoader from '../../../components/loaders/CardLoader'
import { cartSelectors } from '../../Cart/cartSlice'
import { AccountTypes } from '../../../util/constants'

const ClassDetails = ({ location, match, history }) => {
    const dispatch = useDispatch()

    const isFetching = useSelector(classDetailsSelectors.isFetching)
    const hasError = useSelector(classDetailsSelectors.hasError)
    const errorMessage = useSelector(classDetailsSelectors.errorMessage)
    const classDetails = useSelector(classDetailsSelectors.classDetails)
    const relatedClasses = useSelector(classDetailsSelectors.relatedClasses)

    useEffect(() => {
        if (hasError) {
            notification.failure({
                title: 'Something went wrong',
                description: errorMessage,
            })
        }
    }, [isFetching])

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        const { id } = match.params
        dispatch(getClass(id))
        return () => {
            dispatch(classDetailsActions.reset())
        }
    }, [match.params.id])

    const [modalOpen, toggleModalOpen] = useState(false)

    const isLoggedIn = useSelector(userSelectors.isLoggedIn)

    const onCheckout = async ({ invites, numFree }) => {
        const [loader] = await notification.loading({
            title: 'Processing',
        })
        await dispatch(
            addClassToCart({
                id: match.params.id,
                invites,
                numFree,
                classInfo: classDetails,
            })
        )
        loader()
        dispatch(userActions.setRedirectUrl(location.pathname))
        if (!isLoggedIn) {
            history.push('/app/auth/sign-in')
        } else {
            history.push('/app/cart')
        }
    }

    const handleRegistration = () => {
        if (!isLoggedIn) {
            history.push('/app/auth/sign-in')
            notification.info({
                title: 'Please sign in',
            })
            return
        }
        toggleModalOpen(true)
    }

    return (
        <>
            <AuthenticatedHeader />
            {isFetching ? (
                <Loading inline />
            ) : (
                <div className={classes.mainContent}>
                    <div className={classes.classTop}>
                        <Image
                            src={
                                classDetails.thumbnail
                                    ? getImageURL(classDetails.thumbnail)
                                    : placeholder
                            }
                            alt="Class Thumbnail"
                            className={classes.classTopImage}
                            errorImageUrl={placeholder}
                        />
                        <div className={classes.classTopText}>
                            <h6 className={classes.classTopCategory}>
                                {classDetails.catName}
                            </h6>
                            <h1 className={classes.classTopClassName}>
                                {classDetails.title}
                            </h1>
                        </div>
                    </div>
                    <div className={classes.classDataMobile}>
                        <p className={classes.classDataPrice}>
                            {convertToFormatted(classDetails.price || 0, true)}
                        </p>
                        <div className={classes.classDataButtons}>
                            <Button
                                block
                                style={{
                                    cursor: classDetails.isEnrolled
                                        ? 'not-allowed'
                                        : 'cursor',
                                }}
                                onClick={() =>
                                    !classDetails.isEnrolled &&
                                    toggleModalOpen(true)
                                }
                            >
                                {classDetails.isEnrolled
                                    ? 'Registered'
                                    : 'Register'}
                            </Button>
                            {!classDetails.isEnrolled && (
                                <Button iconOnly label={'Add to wishlist'}>
                                    <ButtonIcon
                                        icon={<HeartIcon width={'100%'} />}
                                    />
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={classes.classBody}>
                        <div className={classes.classData}>
                            <p className={classes.classDataPrice}>
                                {convertToFormatted(
                                    classDetails.price || 0,
                                    true
                                )}
                            </p>
                            <div className={classes.classDataButtons}>
                                <Button
                                    style={{
                                        cursor: classDetails.isEnrolled
                                            ? 'not-allowed'
                                            : 'cursor',
                                    }}
                                    block
                                    onClick={
                                        !classDetails.isEnrolled &&
                                        handleRegistration
                                    }
                                >
                                    {classDetails.isEnrolled
                                        ? 'Registered'
                                        : 'Register'}
                                </Button>
                                {!classDetails.isEnrolled && (
                                    <Button iconOnly label={'Add to wishlist'}>
                                        <ButtonIcon
                                            icon={<HeartIcon width={'100%'} />}
                                        />
                                    </Button>
                                )}
                            </div>
                            <div className={classes.classDataSection}>
                                <h1 className={classes.classDataHeader}>
                                    Date and Time
                                </h1>
                                <p className={classes.classDataText}>
                                    {classDetails.starttime}.
                                </p>
                                <p className={classes.classDataText}>
                                    {moment(classDetails.startdate).format(
                                        'dddd, Do of MMMM, YYYY'
                                    )}
                                    .
                                </p>
                            </div>
                            <div>
                                <h1 className={classes.classDataHeader}>
                                    Location
                                </h1>
                                <p className={classes.classDataText}>
                                    {classDetails.location}
                                </p>
                            </div>
                        </div>
                        <div className={classes.classSections}>
                            <div className={classes.classSection}>
                                <h1 className={classes.classSectionHeader}>
                                    Class Description
                                </h1>
                                <p className={classes.classSectionText}>
                                    {classDetails.coursedescription}
                                </p>
                            </div>
                            <div>
                                <h1 className={classes.classSectionHeader}>
                                    Extra Details
                                </h1>
                                {classDetails.objectives.map(
                                    (objective, idx) => (
                                        <p className={classes.classSectionText}>
                                            {idx + 1}. {objective}
                                        </p>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={classes.relatedClasses}>
                        <h1 className={classes.relatedClassesHeader}>
                            Related Courses
                        </h1>
                        <div className={classes.relatedClassesGrid}>
                            {isFetching ? (
                                <>
                                    <CardLoader />
                                    <CardLoader />
                                    <CardLoader />
                                </>
                            ) : (
                                relatedClasses
                                    .slice(0, 3)
                                    .map(classObj => (
                                        <ClassCard
                                            url={
                                                classObj.thumbnail
                                                    ? getImageURL(
                                                          classObj.thumbnail
                                                      )
                                                    : placeholder
                                            }
                                            category={classObj.catName}
                                            attendees={classObj.enrolled}
                                            {...classObj}
                                            date={classObj.startdate}
                                            time={classObj.starttime}
                                            key={classObj.classid}
                                            classid={classObj.classid}
                                            className={classes.class}
                                        />
                                    ))
                            )}
                        </div>
                    </div>
                </div>
            )}
            <Footer />
            <InviteUsersModal
                modalOpen={modalOpen}
                toggleModalOpen={toggleModalOpen}
                classDetails={classDetails}
                onSubmit={onCheckout}
            />
        </>
    )
}

const InviteUsersModal = ({
    modalOpen,
    toggleModalOpen,
    classDetails,
    onSubmit,
}) => {
    const user = useSelector(userSelectors.userDetails)
    const isLoggedIn = useSelector(userSelectors.isLoggedIn)

    const subscriber =
        String(user.accounttype).toLowerCase() ===
        AccountTypes.Subscriber?.toLowerCase()
    const paid =
        String(user.accounttype).toLowerCase() ===
        AccountTypes.Paid?.toLowerCase()

    const items = useSelector(cartSelectors.cartItems)

    const userFreeClasses = getFreeClassesLeft({
        items,
        userFreeClasses: user.freeClasses,
    })

    const initialSlot = useMemo(() => {
        if (isLoggedIn) {
            return {
                name: `${String(user.firstname)} ${String(user.lastname)}`,
                email: user.email,
            }
        } else {
            return { name: '', email: '' }
        }
    }, [isLoggedIn])

    const resetSlots = setFieldValue =>
        setFieldValue('slots', [{ ...initialSlot }])

    const getSlotStats = values => {
        const validSlots = values.slots.filter(
            ({ name, email }) => !!name?.trim() && !!email?.trim()
        )
        const numValidSlots = validSlots.length
        return {
            paid: Math.max(0, numValidSlots - userFreeClasses),
            free: Math.min(userFreeClasses, numValidSlots),
        }
    }

    const renderInputs = (setFieldValue, slots) => num => {
        if (slots.length > num) {
            const newSlots = slots.slice(0, num)
            setFieldValue('slots', newSlots)
        } else if (slots.length < num) {
            const addSlots = [...Array(num - slots.length)].map(() => {
                return {
                    ...{ name: '', email: '' },
                }
            })
            const newSlots = [...slots, ...addSlots]
            setFieldValue('slots', newSlots)
        }
    }

    const submit = values => {
        onSubmit({
            invites: values.slots,
            numFree: getSlotStats(values).free,
        })
    }

    // Formik uses deep equality to check if it should reinitialize
    // So we can't use that, as our initialSlot is the same at all times
    const handleDismiss = setFieldValue => () => {
        toggleModalOpen(false)
        resetSlots(setFieldValue)
    }

    return (
        <Formik
            initialValues={{
                slots: [{ ...initialSlot }],
            }}
            onSubmit={submit}
            validationSchema={InviteUserSchema}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <Modal
                    open={modalOpen}
                    onRequestDismiss={handleDismiss(setFieldValue)}
                >
                    <div className={classes.registrationModal}>
                        <div className={classes.registrationModalTop}>
                            <h1 className={classes.registrationModalTopTitle}>
                                Registration
                            </h1>
                            <button
                                className={
                                    classes.registrationModalTopCancelButton
                                }
                                onClick={handleDismiss(setFieldValue)}
                            >
                                <CancelIcon
                                    alt="Close Modal"
                                    className={
                                        classes.registrationModalTopCancelButtonImage
                                    }
                                />
                            </button>
                        </div>
                        {isLoggedIn && subscriber && userFreeClasses > 0 && (
                            <div className={classes.registrationModalFree}>
                                You have used {getSlotStats(values).free}
                                of {userFreeClasses} free slots
                            </div>
                        )}
                        {isLoggedIn && paid && (
                            <div className={classes.registrationModalFree}>
                                You have no free slots.
                                <span className={classes.orange}>
                                    SUBSCRIBE
                                </span>
                                to get access to free class slots monthly
                            </div>
                        )}
                        <div className={classes.registrationModalBody}>
                            <div className={classes.registrationModalInfoCard}>
                                <h1
                                    className={
                                        classes.registrationModalInfoCardTitle
                                    }
                                >
                                    {classDetails.title}
                                </h1>
                                <p
                                    className={
                                        classes.registrationModalInfoCardAddress
                                    }
                                >
                                    {classDetails.location}
                                </p>
                            </div>
                            <div className={classes.registrationModalRow}>
                                <div
                                    className={
                                        classes.registrationModalSlotsRow
                                    }
                                >
                                    <span
                                        className={
                                            classes.registrationModalSlotsRowText
                                        }
                                    >
                                        Select No of Slots
                                    </span>
                                    <Dropdown
                                        options={Array(100)
                                            .fill(0)
                                            .map((_, idx) => idx + 1)}
                                        initialValue={values.slots.length}
                                        onChange={renderInputs(
                                            setFieldValue,
                                            values.slots
                                        )}
                                    />
                                </div>
                                <div
                                    className={
                                        classes.registrationModalPriceWrapper
                                    }
                                >
                                    <span
                                        className={
                                            classes.registrationModalPriceLabel
                                        }
                                    >
                                        Total Balance
                                    </span>
                                    <span
                                        className={
                                            classes.registrationModalPrice
                                        }
                                    >
                                        {convertToFormatted(
                                            getSlotStats(values).paid *
                                                classDetails.price,
                                            true
                                        )}
                                    </span>
                                </div>
                            </div>
                            {values.slots.length > 0 && (
                                <div>
                                    <h1
                                        className={
                                            classes.registrationModalEmailsHeader
                                        }
                                    >
                                        Enter the email address of your invitees
                                    </h1>
                                    <div
                                        className={
                                            classes.registrationModalEmailsInputs
                                        }
                                    >
                                        {values.slots.map(
                                            ({ name, email }, index) => (
                                                <div
                                                    className={
                                                        classes.registrationModalEmailsInputRow
                                                    }
                                                    key={index}
                                                >
                                                    <ValidatedFormInput
                                                        placeholder={
                                                            'Enter email address of invitee'
                                                        }
                                                        name={`slots[${index}].email`}
                                                    />
                                                    <ValidatedFormInput
                                                        name={`slots[${index}].name`}
                                                        placeholder={
                                                            'Name of Invitee'
                                                        }
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={classes.registrationModalFooter}>
                            <span
                                className={classes.registrationModalFooterText}
                            >
                                {/*Show number of free courses only if they're logged in*/}
                                {isLoggedIn && (
                                    <>
                                        {getSlotStats(values).free} free slot
                                        {getSlotStats(values).free !== 1
                                            ? 's'
                                            : ''}
                                        ,{' '}
                                    </>
                                )}
                                {getSlotStats(values).paid} paid slot
                                {getSlotStats(values).paid !== 1 ? 's' : ''}
                            </span>
                            <Button onClick={handleSubmit}>Checkout</Button>
                        </div>
                    </div>
                </Modal>
            )}
        </Formik>
    )
}

export default ClassDetails
